import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Navbar, { ScrollIndicator } from "./Navbar";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsAppButton";

const IMAGES = {
  heroBg: "/images/imageweb_1.jpg",
  product1: "/images/imageweb_4.jpg",
  product2: "/images/imageweb_12.jpg",
  news1: "/images/imageweb_9.jpg",
  news2: "/images/imageweb_5.jpg",
  news3: "/images/imageweb_2.jpg",
  staff1: "/images/staff-1.jpg",
  staff2: "/images/staff-2.jpg",
  staff3: "/images/staff-1.jpg",
  staff4: "/images/staff-1.jpg",
  staff5: "/images/staff-1.jpg",
};

const staffMembers = [
  { id: 1, name: "Staff 1", position: "Direktur Utama", img: IMAGES.staff1 },
  {
    id: 2,
    name: "Staff 2",
    position: "Manajer Operasional",
    img: IMAGES.staff2,
  },
  { id: 3, name: "Staff 3", position: "Manajer Keuangan", img: IMAGES.staff3 },
  { id: 4, name: "Staff 4", position: "Manajer Pemasaran", img: IMAGES.staff4 },
  { id: 5, name: "Staff 5", position: "Staf Administrasi", img: IMAGES.staff5 },
];

const featuredProducts = [
  {
    id: 1,
    category: "PERTANIAN",
    name: "Kopi Puhu",
    img: IMAGES.product1,
    description:
      "Kopi arabika pilihan dari kebun lereng Gunung Puhu, Bandung. Diproses secara tradisional oleh petani lokal dengan cita rasa fruity dan aroma bunga yang khas.",
  },
  {
    id: 2,
    category: "KERAJINAN",
    name: "Kerajinan Anyaman",
    img: IMAGES.product2,
    description:
      "Kerajinan anyaman handmade dari pengrajin desa. Tersedia dalam berbagai model: tas, peci, dan sandal yang pasti ramah lingkungan.",
  },
];

const newsItems = [
  {
    id: 1,
    title: "Panen Raya Kopi Puhu 2025 Hasilkan 12 Ton Biji Kopi Berkualitas",
    date: "15 November 2025",
    img: IMAGES.news1,
    description:
      "Musim panen kopi tahun ini memberikan hasil terbaik dalam 5 tahun terakhir. Petani binaan BUM Desa berhasil memanen 12 ton biji kopi grade A.",
  },
  {
    id: 2,
    title: "BUM Desa Warga Dekat Raih Penghargaan BUMDes Terbaik Jabar 2025",
    date: "03 Oktober 2025",
    img: IMAGES.news2,
    description:
      "Gubernur Jawa Barat memberikan penghargaan BUMDes Terbaik kepada BUM Desa Warga Dekat Bandung atas kontribusi nyata dalam pemberdayaan ekonomi desa.",
  },
  {
    id: 3,
    title: "Pelatihan Pengolahan Produk Pertanian untuk 50 Warga Desa",
    date: "18 September 2025",
    img: IMAGES.news3,
    description:
      "BUM Desa menggelar pelatihan pengolahan produk pertanian bersama Dinas Pertanian Kabupaten Bandung, diikuti 50 warga dari 3 dusun.",
  },
];

export default function Homepage({ isAdmin, AdminSection }) {
  const [staffIdx, setStaffIdx] = useState(0);
  const navigate = useNavigate();

  const nextStaff = () =>
    setStaffIdx((p) => (p + 3 >= staffMembers.length ? 0 : p + 1));
  const prevStaff = () =>
    setStaffIdx((p) =>
      p === 0 ? Math.max(0, staffMembers.length - 3) : p - 1,
    );
  const visibleStaff = staffMembers.slice(staffIdx, staffIdx + 3);

  return (
    <div className="min-h-screen bg-white font-serif page-transition">
      <style>{`
        .hp-hero { height:420px; max-height:420px; min-height:320px; position:relative; display:flex; align-items:center; justify-content:center; background-size:cover; background-position:center; overflow:hidden; }
        .hp-hero-overlay { position:absolute; inset:0; background:linear-gradient(160deg,rgba(20,40,28,0.75) 0%,rgba(61,79,69,0.52) 60%,rgba(15,30,20,0.82) 100%); }
        .hp-hero-content { position:relative; z-index:10; text-align:center; padding:0 1.5rem; animation:hpFadeUp 1s ease 0.2s both; }
        @keyframes hpFadeUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
        .hp-eyebrow { font-size:.62rem; letter-spacing:.32em; color:#b8c5ba; text-transform:uppercase; margin-bottom:1rem; }
        .hp-title { font-size:clamp(1.9rem,4.5vw,3.4rem); color:#fff; font-weight:300; line-height:1.18; margin-bottom:1.2rem; }
        .hp-sub { font-size:.87rem; color:rgba(255,255,255,.82); max-width:560px; margin:0 auto 2.2rem; line-height:1.78; }
        .card-wrap-hover .card-img { transition:transform .45s ease; }
        .card-wrap-hover:hover .card-img { transform:scale(1.05); }
      `}</style>

      <Navbar />
      <WhatsAppButton />

      {/* HERO */}
      <section
        className="hp-hero"
        style={{ backgroundImage: `url(${IMAGES.heroBg})` }}
      >
        <div className="hp-hero-overlay" />
        <div className="hp-hero-content">
          <p className="hp-eyebrow">Selamat Datang Di</p>
          <h1 className="hp-title">BUM Desa Warga Dekat Bandung</h1>
          <p className="hp-sub">
            Badan Usaha Milik Desa yang berkomitmen memberdayakan ekonomi lokal,
            mengembangkan potensi desa, dan meningkatkan kesejahteraan
            masyarakat melalui produk dan layanan berkualitas.
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Link
              to="/about-us"
              className="bg-white text-[#3d4f45] px-8 py-3 rounded-full text-xs tracking-widest font-semibold hover:bg-[#b8c5ba] transition"
            >
              TENTANG KAMI
            </Link>
            <Link
              to="/products"
              className="border border-white text-white px-8 py-3 rounded-full text-xs tracking-widest hover:bg-white hover:text-[#3d4f45] transition"
            >
              LIHAT PRODUK
            </Link>
          </div>
        </div>
      </section>

      {/* WELCOME */}
      <section id="welcome-section" className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl mb-2 text-[#3d4f45] font-light">
            Selamat Datang Di
          </h2>
          <h2 className="text-3xl mb-8 text-[#3d4f45] font-light">
            BUM Desa Warga Dekat Bandung
          </h2>
          <p className="text-gray-600 leading-relaxed mb-8 text-sm">
            BUM Desa Warga Dekat Bandung adalah badan usaha milik desa yang
            didirikan untuk mengelola potensi lokal secara profesional. Kami
            mengelola usaha pertanian, kerajinan, dan jasa dengan prinsip
            transparansi, kemandirian, dan keberlanjutan — membuka peluang kerja
            dan meningkatkan pendapatan asli desa.
          </p>
          <Link
            to="/about-us"
            className="inline-block bg-[#3d4f45] text-white px-8 py-3 rounded-full text-sm hover:bg-[#4a5a50] transition"
          >
            PELAJARI LEBIH LANJUT
          </Link>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 px-6 bg-[#f5f7f6]">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-3xl mb-3 text-[#3d4f45] font-light">
            Mengapa Memilih Kami?
          </h2>
          <p className="text-gray-500 text-sm">
            Berdiri sejak 2016, kami terus tumbuh bersama masyarakat desa.
          </p>
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          {[
            [
              "8+",
              "Tahun Pengalaman",
              "Berpengalaman dalam pengelolaan usaha berbasis desa sejak 2016.",
            ],
            [
              "100%",
              "Produk Lokal",
              "Seluruh produk dihasilkan oleh warga dan sumber daya lokal desa.",
            ],
            [
              "500+",
              "Pelanggan Puas",
              "Melayani pelanggan dari seluruh Bandung dan kota-kota sekitarnya.",
            ],
          ].map(([val, label, desc]) => (
            <div
              key={label}
              className="bg-white p-8 text-center shadow-sm rounded-xl border border-gray-100"
            >
              <div className="text-5xl font-bold text-[#3d4f45] mb-3">
                {val}
              </div>
              <p className="text-[#3d4f45] font-semibold text-sm mb-2">
                {label}
              </p>
              <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl text-center mb-2 text-[#3d4f45] font-light">
            Produk Unggulan Kami
          </h2>
          <p className="text-center text-gray-500 text-sm mb-12">
            Diproduksi lokal, berkualitas nasional.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {featuredProducts.map((p) => (
              <div
                key={p.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition cursor-pointer card-wrap-hover"
                onClick={() => navigate(`/products/${p.id}`)}
              >
                <div className="h-64 overflow-hidden bg-[#b8c5ba]">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="card-img w-full h-full object-cover"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
                <div className="p-6">
                  <div className="text-xs text-[#3d4f45] mb-2 tracking-wider">
                    {p.category}
                  </div>
                  <h3 className="text-2xl mb-3 text-[#3d4f45]">{p.name}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">
                    {p.description}
                  </p>
                  <button className="bg-[#3d4f45] text-white px-6 py-2 rounded-full text-xs tracking-wider hover:bg-[#4a5a50] transition">
                    DETAIL PRODUK
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/products"
              className="border border-[#3d4f45] text-[#3d4f45] px-8 py-3 rounded-full text-xs tracking-wider hover:bg-[#3d4f45] hover:text-white transition"
            >
              LIHAT SEMUA PRODUK
            </Link>
          </div>
        </div>
      </section>

      {/* STAFF */}
      <section className="py-24 px-6 bg-[#f5f7f6]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl text-center mb-12 text-[#3d4f45] font-light">
            Pengurus BUM Desa
          </h2>
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {visibleStaff.map((s) => (
                <div key={s.id} className="text-center">
                  <div className="w-36 h-36 rounded-full mx-auto mb-4 overflow-hidden bg-gray-200 shadow-md">
                    <img
                      src={s.img}
                      alt={s.name}
                      className="w-full h-full object-cover"
                      onError={(e) => (e.target.style.display = "none")}
                    />
                  </div>
                  <h3 className="text-base font-semibold text-[#3d4f45]">
                    {s.name}
                  </h3>
                  <p className="text-sm text-gray-500">{s.position}</p>
                </div>
              ))}
            </div>
            <button
              onClick={prevStaff}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <ChevronLeft size={22} className="text-[#3d4f45]" />
            </button>
            <button
              onClick={nextStaff}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <ChevronRight size={22} className="text-[#3d4f45]" />
            </button>
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl text-center mb-2 text-[#3d4f45] font-light">
            Berita Terkini
          </h2>
          <p className="text-center text-gray-500 text-sm mb-12">
            Informasi terbaru dari BUM Desa Warga Dekat Bandung.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {newsItems.map((n) => (
              <div
                key={n.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition cursor-pointer card-wrap-hover"
                onClick={() => navigate(`/news/${n.id}`)}
              >
                <div className="h-48 overflow-hidden bg-[#b8c5ba]">
                  <img
                    src={n.img}
                    alt={n.title}
                    className="card-img w-full h-full object-cover"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs text-gray-400 mb-2">{n.date}</p>
                  <h3 className="text-sm font-semibold mb-2 text-[#3d4f45] leading-snug">
                    {n.title}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">
                    {n.description}
                  </p>
                  <span className="text-xs text-[#3d4f45] hover:underline">
                    Baca Selengkapnya →
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/news"
              className="border border-[#3d4f45] text-[#3d4f45] px-8 py-3 rounded-full text-xs tracking-wider hover:bg-[#3d4f45] hover:text-white transition"
            >
              LIHAT SEMUA BERITA
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24 px-6"
        style={{
          background: "linear-gradient(135deg,#3d4f45 60%,#5a7060 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl mb-4 text-white font-light">
            Tertarik Bergabung atau Bermitra?
          </h2>
          <p className="text-[#b8c5ba] mb-8 text-sm leading-relaxed max-w-2xl mx-auto">
            Mari bersama membangun ekonomi desa yang lebih kuat. Kami terbuka
            untuk kemitraan, pemesanan produk, dan kolaborasi program
            pemberdayaan masyarakat.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-[#3d4f45] px-8 py-3 rounded-full text-xs tracking-widest font-semibold hover:bg-[#b8c5ba] transition"
          >
            HUBUNGI KAMI
          </Link>
        </div>
      </section>

      {isAdmin && (
        <div className="max-w-6xl mx-auto px-6 pb-8">
          <hr className="my-8 border-gray-200" />
          <AdminSection />
        </div>
      )}
      <Footer />
    </div>
  );
}
